/**
 * Platform-agnostic canonical log accumulator.
 *
 * Usage (create → decorate → emit):
 *
 *   createCanonicalContext('CanonicalAuthFlow', operationId, { authFlow: 'general', platform: 'ios' })
 *   decorateCanonical(operationId, { otpRequestDurationMs: 412 })
 *   emitCanonical(operationId, 'success')
 *
 * Register a platform-specific emitter once at app startup via registerCanonicalEmitter.
 * The emitter's isEnabled() is the gate check; createCanonicalContext is a no-op when disabled.
 */

export type CanonicalFields = Record<string, string | number | boolean>

export interface CanonicalEmitter {
  /** Returns true when the feature gate is on. Called on create and emit. */
  isEnabled: () => boolean
  /** Fires once at terminal outcome with the fully assembled field set. */
  emit: (eventType: string, fields: CanonicalFields) => void
}

type CanonicalLogContext = {
  eventType: string
  startedAtMs: number
  fields: CanonicalFields
  emitted: boolean
}

const activeContexts = new Map<string, CanonicalLogContext>()

// 30 min covers the longest realistic consumer auth/form lifecycle.
const CONTEXT_TTL_MS = 30 * 60 * 1000
// Soft cap: evict oldest contexts if the map grows unexpectedly.
const MAX_ACTIVE_CONTEXTS = 200
const EVICTION_INTERVAL_MS = 60_000

let evictionTimer: ReturnType<typeof setInterval> | null = null

const runEviction = () => {
  try {
    const now = Date.now()

    activeContexts.forEach((ctx, key) => {
      if (ctx.emitted || now - ctx.startedAtMs > CONTEXT_TTL_MS) {
        activeContexts.delete(key)
      }
    })

    if (activeContexts.size > MAX_ACTIVE_CONTEXTS) {
      const entries = Array.from(activeContexts.entries()).sort((a, b) => a[1].startedAtMs - b[1].startedAtMs)
      const excess = activeContexts.size - MAX_ACTIVE_CONTEXTS
      for (let i = 0; i < excess; i++) {
        activeContexts.delete(entries[i][0])
      }
    }

    if (activeContexts.size === 0 && evictionTimer !== null) {
      clearInterval(evictionTimer)
      evictionTimer = null
    }
  } catch {
    // Eviction failures must never crash the app
  }
}

const startEvictionTimer = () => {
  if (evictionTimer === null) {
    evictionTimer = setInterval(runEviction, EVICTION_INTERVAL_MS)
    // In Node/vitest environments, unref() prevents the interval from keeping
    // the process alive after all tests complete.
    ;(evictionTimer as unknown as { unref?: () => void }).unref?.()
  }
}

/** Visible for testing only. */
export const _stopEvictionTimer = () => {
  if (evictionTimer !== null) {
    clearInterval(evictionTimer)
    evictionTimer = null
  }
}

/** Visible for testing only. */
export const _runEviction = runEviction

/** Visible for testing only. */
export const _getActiveContexts = () => activeContexts

let _emitter: CanonicalEmitter | null = null

/**
 * Register the platform-specific emitter. Call once at app startup before any
 * canonical context is created.
 */
export const registerCanonicalEmitter = (emitter: CanonicalEmitter): void => {
  _emitter = emitter
}

/** Visible for testing only. */
export const _resetEmitter = () => {
  _emitter = null
}

/**
 * Allocate an accumulator context for the given operation. No-op when the
 * feature gate is off (emitter.isEnabled() returns false) or when no emitter
 * is registered.
 */
export const createCanonicalContext = (
  eventType: string,
  operationId: string,
  initialFields: Record<string, string | number | boolean | null | undefined>
): void => {
  try {
    if (!_emitter?.isEnabled()) return

    const fields: CanonicalFields = {}
    for (const [key, value] of Object.entries(initialFields)) {
      if (value != null) {
        fields[key] = value
      }
    }

    activeContexts.set(operationId, {
      eventType,
      startedAtMs: Date.now(),
      fields,
      emitted: false,
    })
    startEvictionTimer()
  } catch {
    // Canonical failures must never crash the app
  }
}

/**
 * Merge additional fields into the context accumulator. No-op if the context
 * does not exist or has already been emitted.
 */
export const decorateCanonical = (
  operationId: string,
  fields: Record<string, string | number | boolean | null | undefined>
): void => {
  try {
    const ctx = activeContexts.get(operationId)
    if (!ctx) return
    for (const [key, value] of Object.entries(fields)) {
      if (value != null) {
        ctx.fields[key] = value
      }
    }
  } catch {
    // Canonical failures must never crash the app
  }
}

/**
 * Emit the canonical event at the terminal outcome. Calls the registered
 * emitter exactly once per context; subsequent calls for the same operationId
 * are no-ops (double-emit guard). The context is always deleted after emit.
 */
export const emitCanonical = (operationId: string, outcome: string): void => {
  try {
    const ctx = activeContexts.get(operationId)
    if (!ctx || ctx.emitted) return

    try {
      ctx.emitted = true
      if (!_emitter?.isEnabled()) return

      const now = Date.now()
      const allFields: CanonicalFields = {
        ...ctx.fields,
        outcome,
        totalDurationMs: now - ctx.startedAtMs,
        emittedAt: now,
      }

      _emitter.emit(ctx.eventType, allFields)
    } finally {
      // Always delete so no context leaks on gate-check error or any other throw.
      activeContexts.delete(operationId)
    }
  } catch {
    // Canonical failures must never crash the app
  }
}
